import React, { useMemo } from 'react';

import { TiptapRichTextEditor } from 'components/DesignSystems/New/TiptapRichTextEditor';
import AutomatedLayerStatsSectionPanel, {
  isAutomatedLayerStatsSection,
} from 'components/DisasterGPT/AutomatedLayerStatsSectionPanel';
import {
  useSitrepSectionImages,
  type SitrepSectionType,
} from 'components/DisasterGPT/hooks/useSitrepsV2';
import { ReportsGeneratingPlaceholder } from 'components/Reports/ReportsGeneratingPlaceholder';
import AutomatedEntityListSectionPanel, {
  isAutomatedActionsSection,
  isAutomatedIncidentsSection,
  isAutomatedOrgEventsSection,
} from './automated/AutomatedEntityListSectionPanel';
import { ReportMentionDetailDialog } from './mentions/ReportMentionDetailDialog';
import { useReportMentionDialog } from './mentions/useReportMentionDialog';
import { useReportMentionItems } from './mentions/useReportMentionItems';
import { sitrepSectionDisplay } from './document/sitrepSectionDisplay';
import SitrepTasksActionsPanel from 'components/Reports/SitrepTasksActionsPanel';
import DynamicSectionDataToggle from './section/DynamicSectionDataToggle';
import { useDynamicSectionData } from './section/useDynamicSectionData';
import type { GetSitrepsResponse } from './reports.types';

type ReadOnlyReportsSectionDisplayProps = {
  section: SitrepSectionType;
  sitrepId?: string;
  sitrep?: Pick<
    GetSitrepsResponse,
    'id' | 'aor_id' | 'incident_id' | 'approval_status'
  >;
  /** Current report: Live Data analytics can rerun their queries. */
  enableLiveAnalyticsRefresh?: boolean;
};

const ReadOnlyReportsSectionDisplay: React.FC<
  ReadOnlyReportsSectionDisplayProps
> = ({ section, sitrepId, sitrep, enableLiveAnalyticsRefresh = false }) => {
  const resolvedSitrepId = sitrepId || sitrep?.id || '';
  const latestVersion = useMemo(
    () =>
      sitrepSectionDisplay(section).display ?? section.section_versions?.[0],
    [section]
  );

  const { data: sitrepImages } = useSitrepSectionImages(section.id);
  const { items: mentionItems } = useReportMentionItems();
  const {
    mention: activeMention,
    open: mentionDialogOpen,
    onOpenChange: onMentionDialogOpenChange,
    onMentionClick,
  } = useReportMentionDialog();
  const dynamicData = useDynamicSectionData({
    section,
    sitrepId: resolvedSitrepId,
    approvalStatus: sitrep?.approval_status,
  });

  const sectionText = useMemo(
    () => latestVersion?.sitrep_section_text || '',
    [latestVersion?.sitrep_section_text]
  );

  const dataString = dynamicData.showToggle
    ? dynamicData.dataString
    : latestVersion?.data_string;
  const hideRun = true;
  const useLiveEnrichment = dynamicData.showToggle
    ? dynamicData.useLiveEnrichment
    : false;

  const toggle = dynamicData.showLocalToggle ? (
    <div className="tw:mb-2">
      <DynamicSectionDataToggle
        approvalStatus={sitrep?.approval_status}
        mode={dynamicData.mode}
        onModeChange={dynamicData.setMode}
        disabled={dynamicData.isPreviewLoading}
      />
    </div>
  ) : null;

  const liveAnalyticsRefresh =
    enableLiveAnalyticsRefresh &&
    isAutomatedLayerStatsSection(section) &&
    dynamicData.showToggle &&
    dynamicData.mode === 'live';

  if (isAutomatedLayerStatsSection(section) && resolvedSitrepId) {
    return (
      <div className="reports-section tw:text-foreground">
        {toggle}
        {dynamicData.isPreviewLoading &&
        !(
          isAutomatedLayerStatsSection(section) &&
          dynamicData.showToggle &&
          dynamicData.mode === 'live'
        ) ? (
          <ReportsGeneratingPlaceholder message="PRATUS is analyzing data..." />
        ) : (
          <AutomatedLayerStatsSectionPanel
            sitrepId={resolvedSitrepId}
            sitrep={sitrep}
            section={section}
            disabled
            hideRun={hideRun}
            dataStringOverride={dataString}
            onRefreshAnalytics={
              liveAnalyticsRefresh
                ? () => {
                    void dynamicData.refetchPreview();
                  }
                : undefined
            }
            refreshingAnalytics={
              dynamicData.showToggle &&
              dynamicData.mode === 'live' &&
              dynamicData.isPreviewLoading
            }
          />
        )}
      </div>
    );
  }

  if (isAutomatedOrgEventsSection(section) && resolvedSitrepId) {
    return (
      <div className="reports-section tw:text-foreground">
        {toggle}
        {dynamicData.isPreviewLoading ? (
          <ReportsGeneratingPlaceholder />
        ) : (
          <AutomatedEntityListSectionPanel
            kind="orgEvents"
            sitrepId={resolvedSitrepId}
            sitrep={sitrep}
            section={section}
            disabled
            hideRun={hideRun}
            dataStringOverride={dataString}
            useLiveEnrichment={useLiveEnrichment}
          />
        )}
      </div>
    );
  }

  if (isAutomatedIncidentsSection(section) && resolvedSitrepId) {
    return (
      <div className="reports-section tw:text-foreground">
        {toggle}
        {dynamicData.isPreviewLoading ? (
          <ReportsGeneratingPlaceholder />
        ) : (
          <AutomatedEntityListSectionPanel
            kind="incidents"
            sitrepId={resolvedSitrepId}
            sitrep={sitrep}
            section={section}
            disabled
            hideRun={hideRun}
            dataStringOverride={dataString}
            useLiveEnrichment={useLiveEnrichment}
          />
        )}
      </div>
    );
  }

  if (isAutomatedActionsSection(section) && resolvedSitrepId) {
    return (
      <div className="reports-section tw:text-foreground">
        {toggle}
        {dynamicData.isPreviewLoading ? (
          <ReportsGeneratingPlaceholder />
        ) : dynamicData.showToggle && useLiveEnrichment ? (
          <SitrepTasksActionsPanel
            incidentId={sitrep?.incident_id}
            aorId={sitrep?.aor_id}
            canEdit
          />
        ) : (
          <AutomatedEntityListSectionPanel
            kind="actions"
            sitrepId={resolvedSitrepId}
            sitrep={sitrep}
            section={section}
            disabled
            hideRun={hideRun}
            dataStringOverride={dataString}
            useLiveEnrichment={useLiveEnrichment}
          />
        )}
      </div>
    );
  }

  return (
    <div className="reports-section">
      <div>
        {sectionText && (
          <TiptapRichTextEditor
            value={sectionText}
            readOnly
            showToolbar={false}
            minHeight="2rem"
            className="reports-section__html-content"
            normalizeMarkdown
            imageLibrary={sitrepImages || []}
            mentionsEnabled
            mentionItems={mentionItems}
            onMentionClick={onMentionClick}
          />
        )}
      </div>
      <ReportMentionDetailDialog
        mention={activeMention}
        open={mentionDialogOpen}
        onOpenChange={onMentionDialogOpenChange}
      />
    </div>
  );
};

export default ReadOnlyReportsSectionDisplay;
